// @ts-strict-ignore
import type { AttributeConfigurationFormState } from './types';
import type { PublishedAttribute } from '~/dashboard/views/workflows/store/engine/types';
import type { FormKey } from '../useNodeEditorForm/types';
import {
  getChangedFormFieldElementIds,
  getNodeEditorFormHtmlElementId,
} from 'flow/editor/hooks/useWorkflowHistory/helpers';

type AttributeConfigurationField = keyof AttributeConfigurationFormState;

// Min and max render together in a single AcceptedValuesField row, so they share its wrapper.
const ACCEPTED_VALUE_FIELDS: AttributeConfigurationField[] = ['validValueMin', 'validValueMax'];

const toWrapperField = (field: string): string => (
  ACCEPTED_VALUE_FIELDS.includes(field as AttributeConfigurationField)
    ? 'validValueMin'
    : field
);

export function getAttributeConfigurationFieldElementId(
  formKey: FormKey,
  field: AttributeConfigurationField,
): string {
  return getNodeEditorFormHtmlElementId(formKey, toWrapperField(field));
}

export function getChangedAttributeConfigurationFieldElementIds(
  formKey: FormKey,
  current: AttributeConfigurationFormState,
  source: AttributeConfigurationFormState,
): string[] {
  return getChangedFormFieldElementIds({
    formKey, current, source, toWrapperField,
  });
}

/**
 * Maps form data to published attribute data, transforming empty strings to null
 * to match the behavior of the Daffy API endpoint (getPublishedAttributesAddPayload).
 * This ensures consistency between Typhon (local) and Daffy (remote) save paths.
 *
 * @param formData - The form state from react-hook-form
 * @returns Partial published attribute with only updatable fields transformed
 */
export function mapFormDataToPublishedAttributeProp(
  formData: AttributeConfigurationFormState,
): Partial<PublishedAttribute> {
  return {
    // Updatable fields - transform empty strings to null
    attributeName: formData.attributeName,
    defaultValue: formData.defaultValue,
    defaultValueNote: formData.defaultValueNote || null,
    groupingToken: formData.groupingToken || null,
    jqFilter: formData.hasJqFilter ? (formData.jqFilter || null) : null,
    validValueMin: formData.validValueMin || null,
    validValueMax: formData.validValueMax || null,
    validValues: formData.validValues || null,
    hasExpiration: formData.hasExpiration,
    hasJqFilter: formData.hasJqFilter,
    valueExpiresInDays: formData.valueExpiresInDays,
    agentCanCreateValue: formData.agentCanCreateValue,
  };
}

